/**
 * SUPPORT-ONLY. OpenSearch/Kibana-style time range picker — ported from the
 * ocpp-log-visualizer demo. Each bound (start and end) independently supports
 * three modes: Absolute (a specific datetime), Relative ("15 minutes ago"),
 * and Now (the current instant at fetch time). Styled with Tailwind to match
 * the dashboard design system.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

// ─── TimeSpec types ───────────────────────────────────────────────────────────

export type RelUnit = 's' | 'm' | 'h' | 'd' | 'w' | 'M' | 'y';

export type TimeSpec =
  | { mode: 'now' }
  | { mode: 'absolute'; iso: string }
  | { mode: 'relative'; amount: number; unit: RelUnit; dir: 'ago' | 'fromnow'; round: boolean };

const UNIT_LABEL: Record<RelUnit, string> = {
  s: 'Seconds',
  m: 'Minutes',
  h: 'Hours',
  d: 'Days',
  w: 'Weeks',
  M: 'Months',
  y: 'Years',
};
const UNIT_ORDER: RelUnit[] = ['s', 'm', 'h', 'd', 'w', 'M', 'y'];

// ─── Core helpers ────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');

function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function shiftRelative(now: Date, amount: number, unit: RelUnit, dir: 'ago' | 'fromnow'): Date {
  const n = (dir === 'ago' ? -1 : 1) * amount;
  const d = new Date(now.getTime());
  switch (unit) {
    case 's':
      d.setSeconds(d.getSeconds() + n);
      break;
    case 'm':
      d.setMinutes(d.getMinutes() + n);
      break;
    case 'h':
      d.setHours(d.getHours() + n);
      break;
    case 'd':
      d.setDate(d.getDate() + n);
      break;
    case 'w':
      d.setDate(d.getDate() + n * 7);
      break;
    case 'M':
      d.setMonth(d.getMonth() + n);
      break;
    case 'y':
      d.setFullYear(d.getFullYear() + n);
      break;
  }
  return d;
}

function roundDown(d: Date, unit: RelUnit): Date {
  const r = new Date(d.getTime());
  switch (unit) {
    case 's':
      r.setMilliseconds(0);
      break;
    case 'm':
      r.setSeconds(0, 0);
      break;
    case 'h':
      r.setMinutes(0, 0, 0);
      break;
    case 'd':
      r.setHours(0, 0, 0, 0);
      break;
    case 'w':
      r.setHours(0, 0, 0, 0);
      r.setDate(r.getDate() - r.getDay());
      break;
    case 'M':
      r.setHours(0, 0, 0, 0);
      r.setDate(1);
      break;
    case 'y':
      r.setHours(0, 0, 0, 0);
      r.setMonth(0, 1);
      break;
  }
  return r;
}

export function resolveTimeSpec(spec: TimeSpec, now: Date = new Date()): Date {
  switch (spec.mode) {
    case 'now':
      return now;
    case 'absolute':
      return new Date(spec.iso);
    case 'relative': {
      const shifted = shiftRelative(now, spec.amount, spec.unit, spec.dir);
      return spec.round ? roundDown(shifted, spec.unit) : shifted;
    }
  }
}

export function formatSpec(spec: TimeSpec): string {
  switch (spec.mode) {
    case 'now':
      return 'now';
    case 'absolute':
      return toLocalInput(new Date(spec.iso)).replace('T', ' @ ');
    case 'relative': {
      const unit = UNIT_LABEL[spec.unit].toLowerCase();
      const noun = spec.amount === 1 ? unit.replace(/s$/, '') : unit;
      return spec.dir === 'ago'
        ? `~ ${spec.amount} ${noun} ago`
        : `~ ${spec.amount} ${noun} from now`;
    }
  }
}

// ─── URL encoding ────────────────────────────────────────────────────────────

/** Encode a TimeSpec to a URL param string (ISO, "now", or "~15m-ago"). */
export function encodeTimeSpecString(spec: TimeSpec): string {
  if (spec.mode === 'now') return 'now';
  if (spec.mode === 'absolute') return spec.iso;
  return `~${spec.amount}${spec.unit}-${spec.dir}${spec.round ? '-r' : ''}`;
}

/** Decode a URL param string back to a TimeSpec. Returns undefined if unparseable. */
export function decodeTimeSpecString(s: string): TimeSpec | undefined {
  if (!s) return undefined;
  if (s === 'now') return { mode: 'now' };
  if (s.startsWith('~')) {
    const m = s.match(/^~(\d+)([smhdwMy])-(ago|fromnow)(-r)?$/);
    if (m) {
      return {
        mode: 'relative',
        amount: parseInt(m[1], 10),
        unit: m[2] as RelUnit,
        dir: m[3] as 'ago' | 'fromnow',
        round: !!m[4],
      };
    }
    return undefined;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return { mode: 'absolute', iso: s };
  return undefined;
}

// ─── Presets ─────────────────────────────────────────────────────────────────

type OcppPresetId = 'last_15m' | 'last_1h' | 'last_1d' | 'last_3d' | 'last_week' | 'custom';

interface OcppPreset {
  id: OcppPresetId;
  labelKey: string;
  from?: TimeSpec;
  to?: TimeSpec;
}

const OCPP_PRESETS: OcppPreset[] = [
  {
    id: 'last_15m',
    labelKey: 'ocpp_timelog.preset_15m',
    from: { mode: 'relative', amount: 15, unit: 'm', dir: 'ago', round: false },
    to: { mode: 'now' },
  },
  {
    id: 'last_1h',
    labelKey: 'ocpp_timelog.preset_1h',
    from: { mode: 'relative', amount: 1, unit: 'h', dir: 'ago', round: false },
    to: { mode: 'now' },
  },
  {
    id: 'last_1d',
    labelKey: 'ocpp_timelog.preset_1d',
    from: { mode: 'relative', amount: 1, unit: 'd', dir: 'ago', round: false },
    to: { mode: 'now' },
  },
  {
    id: 'last_3d',
    labelKey: 'ocpp_timelog.preset_3d',
    from: { mode: 'relative', amount: 3, unit: 'd', dir: 'ago', round: false },
    to: { mode: 'now' },
  },
  {
    id: 'last_week',
    labelKey: 'ocpp_timelog.preset_week',
    from: { mode: 'relative', amount: 7, unit: 'd', dir: 'ago', round: false },
    to: { mode: 'now' },
  },
  { id: 'custom', labelKey: 'ocpp_timelog.preset_custom' },
];

function specsMatch(a: TimeSpec, b: TimeSpec): boolean {
  if (a.mode !== b.mode) return false;
  if (a.mode === 'now') return true;
  if (a.mode === 'absolute' && b.mode === 'absolute') return a.iso === b.iso;
  if (a.mode === 'relative' && b.mode === 'relative') {
    return a.amount === b.amount && a.unit === b.unit && a.dir === b.dir && a.round === b.round;
  }
  return false;
}

function detectPreset(from: TimeSpec, to: TimeSpec): OcppPresetId {
  for (const p of OCPP_PRESETS) {
    if (p.from && p.to && specsMatch(from, p.from) && specsMatch(to, p.to)) return p.id;
  }
  return 'custom';
}

// ─── SpecEditor (tab panel inside the popover) ───────────────────────────────

type EditorTab = 'absolute' | 'relative' | 'now';

function tabOf(spec: TimeSpec): EditorTab {
  return spec.mode === 'now' ? 'now' : spec.mode === 'absolute' ? 'absolute' : 'relative';
}

function SpecEditor({ spec, onChange }: { spec: TimeSpec; onChange: (s: TimeSpec) => void }) {
  const [tab, setTab] = useState<EditorTab>(tabOf(spec));

  const rel =
    spec.mode === 'relative'
      ? spec
      : { amount: 15, unit: 'm' as RelUnit, dir: 'ago' as const, round: false };
  const absIso = spec.mode === 'absolute' ? spec.iso : new Date().toISOString();

  const tabClass = (t: EditorTab) =>
    `flex-1 py-2 text-xs cursor-pointer bg-transparent border-0 border-b-2 transition-colors ${
      tab === t
        ? 'border-b-denim text-denim font-semibold'
        : 'border-transparent text-grey5 hover:text-grey6'
    }`;

  return (
    <div className='w-80'>
      <div className='flex border-b border-grey2'>
        <button type='button' className={tabClass('absolute')} onClick={() => setTab('absolute')}>
          Absolute
        </button>
        <button type='button' className={tabClass('relative')} onClick={() => setTab('relative')}>
          Relative
        </button>
        <button type='button' className={tabClass('now')} onClick={() => setTab('now')}>
          Now
        </button>
      </div>

      <div className='p-3.5'>
        {tab === 'absolute' && (
          <div className='flex flex-col gap-1.5'>
            <span className='text-xs text-grey5'>date &amp; time</span>
            <input
              type='datetime-local'
              step={1}
              className='w-full box-border bg-grey1 text-grey6 border border-grey3 rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-denim'
              value={toLocalInput(new Date(absIso))}
              onChange={(e) => {
                const d = new Date(e.target.value);
                if (!Number.isNaN(d.getTime()))
                  onChange({ mode: 'absolute', iso: d.toISOString() });
              }}
            />
          </div>
        )}

        {tab === 'relative' && (
          <div className='flex flex-col gap-2.5'>
            <div className='flex gap-2'>
              <input
                type='number'
                min={0}
                className='w-20 box-border bg-grey1 text-grey6 border border-grey3 rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-denim'
                value={rel.amount}
                onChange={(e) =>
                  onChange({
                    mode: 'relative',
                    ...rel,
                    amount: Math.max(0, Number(e.target.value)),
                  })
                }
              />
              <select
                className='flex-1 bg-grey1 text-grey6 border border-grey3 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-denim'
                value={`${rel.unit}|${rel.dir}`}
                onChange={(e) => {
                  const [unit, dir] = e.target.value.split('|') as [RelUnit, 'ago' | 'fromnow'];
                  onChange({ mode: 'relative', ...rel, unit, dir });
                }}
              >
                {(['ago', 'fromnow'] as const).map((dir) =>
                  UNIT_ORDER.map((u) => (
                    <option key={`${u}|${dir}`} value={`${u}|${dir}`}>
                      {UNIT_LABEL[u]} {dir === 'ago' ? 'ago' : 'from now'}
                    </option>
                  )),
                )}
              </select>
            </div>
            <label className='flex items-center gap-2 text-xs text-grey6 cursor-pointer'>
              <input
                type='checkbox'
                checked={rel.round}
                onChange={(e) => onChange({ mode: 'relative', ...rel, round: e.target.checked })}
              />
              Round to the {UNIT_LABEL[rel.unit].toLowerCase().replace(/s$/, '')}
            </label>
            <div className='text-xs text-grey5'>
              Resolves to{' '}
              <span className='font-mono text-grey6'>
                {toLocalInput(resolveTimeSpec({ mode: 'relative', ...rel })).replace('T', ' @ ')}
              </span>
            </div>
          </div>
        )}

        {tab === 'now' && (
          <div className='flex flex-col gap-2.5'>
            <div className='text-xs text-grey5 leading-relaxed'>
              Sets this bound to the current time, evaluated when you fetch.
            </div>
            <button
              type='button'
              onClick={() => onChange({ mode: 'now' })}
              className={`px-3 py-1.5 text-sm rounded border transition-colors ${
                spec.mode === 'now'
                  ? 'bg-denim text-white border-denim'
                  : 'bg-white text-grey6 border-grey3 hover:border-denim hover:text-denim'
              }`}
            >
              Set to now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Bound button + portaled popover ─────────────────────────────────────────

function Bound({
  label,
  spec,
  open,
  onToggle,
  onChange,
}: {
  label: string;
  spec: TimeSpec;
  open: boolean;
  onToggle: () => void;
  onChange: (s: TimeSpec) => void;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [btnRect, setBtnRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (open && btnRef.current) {
      setBtnRect(btnRef.current.getBoundingClientRect());
    } else {
      setBtnRect(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popoverRef.current?.contains(t)) return;
      onToggle();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onToggle]);

  const popoverStyle = btnRect
    ? {
        position: 'fixed' as const,
        top: btnRect.bottom + 8,
        left: Math.max(8, Math.min(btnRect.left, window.innerWidth - 336)),
        zIndex: 9999,
      }
    : null;

  return (
    <>
      <button
        ref={btnRef}
        type='button'
        onClick={onToggle}
        title={`${label} bound`}
        className={`bg-transparent border-0 border-b-2 px-1.5 py-1 font-mono text-xs text-grey6 cursor-pointer whitespace-nowrap transition-colors ${
          open ? 'border-b-denim' : 'border-transparent hover:border-grey3'
        }`}
      >
        {formatSpec(spec)}
      </button>
      {open &&
        popoverStyle &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            className='bg-white border border-grey2 rounded-xl shadow-lg'
            style={popoverStyle}
          >
            <SpecEditor spec={spec} onChange={onChange} />
          </div>,
          document.body,
        )}
    </>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

interface TimeRangePickerProps {
  from: TimeSpec;
  to: TimeSpec;
  onChange: (from: TimeSpec, to: TimeSpec) => void;
}

export const TimeRangePicker = ({ from, to, onChange }: TimeRangePickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<'from' | 'to' | null>(null);
  const activePreset = detectPreset(from, to);

  return (
    <div className='flex flex-col gap-2'>
      {/* Preset chips */}
      <div className='flex gap-1 p-0.5 bg-grey1 border border-grey2 rounded-lg h-9 items-center box-border w-fit'>
        {OCPP_PRESETS.map((p) => (
          <button
            key={p.id}
            type='button'
            onClick={() => {
              if (p.from && p.to) onChange(p.from, p.to);
            }}
            className={`h-7 px-2.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
              activePreset === p.id
                ? 'bg-white text-grey6 shadow-sm'
                : 'bg-transparent text-grey5 hover:text-grey6'
            }`}
          >
            {t(p.labelKey)}
          </button>
        ))}
      </div>

      {/* From → To bound row */}
      <div className='inline-flex items-center gap-1.5 border border-grey3 rounded-lg bg-grey1 px-2.5 h-9 box-border w-fit'>
        <Bound
          label='Start'
          spec={from}
          open={open === 'from'}
          onToggle={() => setOpen((o) => (o === 'from' ? null : 'from'))}
          onChange={(s) => onChange(s, to)}
        />
        <span className='text-grey4 text-sm select-none'>→</span>
        <Bound
          label='End'
          spec={to}
          open={open === 'to'}
          onToggle={() => setOpen((o) => (o === 'to' ? null : 'to'))}
          onChange={(s) => onChange(from, s)}
        />
      </div>
    </div>
  );
};
